import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckCircle, Shield, Clock, Users, Calendar, BookOpen, Linkedin, X, TrendingDown, HelpCircle, Award, TrendingUp, Target, AlertTriangle, Lightbulb, GraduationCap, DollarSign, Map } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { programData, RAZORPAY_PAYMENT_LINK } from '../mock/mockData';

const LandingPage = () => {
  const navigate = useNavigate();
  
  const handleRegisterClick = () => {
    window.location.href = RAZORPAY_PAYMENT_LINK;
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header - Enhanced Logo & Branding */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          {/* Logo + Brand Name - VISIBLE ON MOBILE */}
          <div className="flex items-center gap-3 md:gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/dh5l5tv8_Financial%20Intelligence%20Academy%20logo%20design.png"
              alt="Financial Intelligence Academy"
              className="h-12 md:h-16 lg:h-20"
            />
            {/* Brand text - now visible on mobile too */}
            <div className="border-l-2 border-gray-300 pl-3 md:pl-4">
              <div className="font-serif text-sm md:text-xl font-bold text-gray-900 leading-tight">
                Financial Intelligence
              </div>
              <div className="text-xs md:text-sm text-gray-600 font-medium">
                Academy
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="flex gap-4 md:gap-6 items-center">
            <button
              onClick={() => navigate('/programs')}
              className="text-gray-700 hover:text-blue-900 transition-colors font-medium text-sm md:text-base"
            >
              Programs
            </button>
            <Button
              onClick={handleRegisterClick}
              className="bg-blue-900 hover:bg-blue-800 text-white px-4 md:px-6 py-2 md:py-2.5 transition-colors font-semibold text-sm md:text-base"
            >
              Register Now
            </Button>
          </div>
        </div>
      </header>

      {/* Hero Section - Responsive Layouts */}
      <section className="relative overflow-hidden">
        
        {/* MOBILE LAYOUT (< 768px) - Image with Better Positioning */}
        <div className="md:hidden relative h-[600px]">
          {/* Background Image - Centered on Faces */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/1f85opha_Gemini_Generated_Image_k9e0gik9e0gik9e0.png')`,
              backgroundSize: 'cover',
              backgroundPosition: 'center center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            {/* Lighter gradient for mobile - faces visible */}
            <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/70"></div>
          </div>

          {/* Text Content */}
          <div className="relative h-full flex items-center px-6">
            <div className="w-full text-center">
              
              {/* Eyebrow */}
              <p className="text-amber-400 text-sm font-semibold tracking-wide uppercase mb-4">
                For Parents of Teens (Ages 13–17)
              </p>

              {/* Headline */}
              <h1 className="font-serif text-3xl text-white mb-6 leading-tight">
                Other kids are learning money early—will yours be left guessing?
              </h1>

              {/* Subheadline */}
              <p className="text-white/90 text-base mb-8 leading-relaxed">
                In just 10 weeks, your teen will learn how money actually works—so they can earn, invest, and make smart decisions before costly mistakes happen.
              </p>

              {/* CTA */}
              <Button
                onClick={handleRegisterClick}
                className="w-full bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold px-8 py-6 text-lg mb-6 shadow-xl"
              >
                Secure Your Seat – ₹299
              </Button>

              {/* Value Bullets */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-center gap-3 text-white/90 text-sm">
                  <TrendingUp size={18} className="text-amber-400 flex-shrink-0" />
                  <span>Learn investing, saving & smart decision-making early</span>
                </div>
                <div className="flex items-center justify-center gap-3 text-white/90 text-sm">
                  <Award size={18} className="text-amber-400 flex-shrink-0" />
                  <span>Build real financial confidence (not just theory)</span>
                </div>
                <div className="flex items-center justify-center gap-3 text-white/90 text-sm">
                  <Target size={18} className="text-amber-400 flex-shrink-0" />
                  <span>Small batch = personal attention</span>
                </div>
              </div>

              {/* Trust Signals - Stacked */}
              <div className="text-white/70 text-xs space-y-2 pt-4 border-t border-white/20">
                <div className="flex items-center justify-center gap-2">
                  <Users size={14} className="text-amber-400" />
                  <span>Limited to 20 Students</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <CheckCircle size={14} className="text-amber-400" />
                  <span>Certificate Upon Completion</span>
                </div>
                <div className="flex items-center justify-center gap-2">
                  <Calendar size={14} className="text-amber-400" />
                  <span>Parent Orientation Required</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* DESKTOP/TABLET LAYOUT (≥ 768px) - Image Background */}
        <div className="hidden md:block relative h-[800px]">
          {/* Background Image */}
          <div 
            className="absolute inset-0"
            style={{
              backgroundImage: `url('https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/1f85opha_Gemini_Generated_Image_k9e0gik9e0gik9e0.png')`,
              backgroundSize: 'cover',
              backgroundPosition: '75% center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            {/* Gradient Overlay */}
            <div className="absolute inset-0 bg-gradient-to-r from-black/95 via-black/70 to-black/25"></div>
          </div>

          {/* Text Content - Left Safe Zone */}
          <div className="relative h-full flex items-center">
            <div className="w-full max-w-7xl mx-auto px-8">
              {/* Text Container - Fixed Width, Left Anchored */}
              <div 
                className="ml-8 lg:ml-12"
                style={{ 
                  maxWidth: '520px',
                  width: '100%'
                }}
              >
                
                {/* Eyebrow */}
                <p className="text-amber-400 text-base font-semibold tracking-wide uppercase mb-5">
                  For Parents of Teens (Ages 13–17)
                </p>

                {/* Headline */}
                <h1 className="font-serif text-4xl lg:text-5xl xl:text-6xl text-white mb-7 leading-[1.2]">
                  Other kids are learning money early—will yours be left guessing?
                </h1>

                {/* Subheadline */}
                <p className="text-white/90 text-lg lg:text-xl mb-10 leading-relaxed">
                  In just 10 weeks, your teen will learn how money actually works—so they can earn, invest, and make smart decisions before costly mistakes happen.
                </p>

                {/* CTA */}
                <Button
                  onClick={handleRegisterClick}
                  className="bg-amber-500 hover:bg-amber-600 text-gray-900 font-bold px-12 py-7 text-xl transition-all shadow-xl hover:shadow-2xl hover:scale-105 duration-200 mb-6"
                >
                  Secure Your Seat – ₹299
                </Button>

                {/* Value Bullets */}
                <div className="space-y-3 mb-6">
                  <div className="flex items-center gap-3 text-white/90 text-base">
                    <TrendingUp size={20} className="text-amber-400 flex-shrink-0" />
                    <span>Learn investing, saving & smart decision-making early</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90 text-base">
                    <Award size={20} className="text-amber-400 flex-shrink-0" />
                    <span>Build real financial confidence (not just theory)</span>
                  </div>
                  <div className="flex items-center gap-3 text-white/90 text-base">
                    <Target size={20} className="text-amber-400 flex-shrink-0" />
                    <span>Small batch = personal attention</span>
                  </div>
                </div>

                {/* Trust Signals */}
                <div className="text-white/60 text-sm flex flex-wrap items-center gap-3 pt-4 border-t border-white/20">
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Users size={14} className="text-amber-400 flex-shrink-0" />
                    <span>Only 20 Seats</span>
                  </span>
                  <span className="text-white/30">•</span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <CheckCircle size={14} className="text-amber-400 flex-shrink-0" />
                    <span>Certificate Included</span>
                  </span>
                  <span className="text-white/30">•</span>
                  <span className="flex items-center gap-1.5 whitespace-nowrap">
                    <Calendar size={14} className="text-amber-400 flex-shrink-0" />
                    <span>Parent Orientation Required</span>
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Instructor Info - Below Hero */}
      <section className="py-4 bg-gray-50 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-gray-600 text-sm md:text-base font-medium">
            Taught live by Muskaan Dewan · Educator & Financial Markets Expert
          </p>
        </div>
      </section>

      {/* What You'll Learn in Parent Orientation Section - PREMIUM LUXURY DESIGN */}
      <section className="py-20 px-6 bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        {/* Spotlight Effect */}
        <div className="absolute inset-0 bg-gradient-radial from-blue-800/30 via-transparent to-transparent opacity-50"></div>
        
        <div className="max-w-4xl mx-auto relative z-10">
          
          {/* Exclusive Badge */}
          <div className="flex justify-center mb-8">
            <div className="bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 text-slate-900 px-8 py-3 rounded-full shadow-lg inline-flex items-center gap-2 border-2 border-yellow-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
              </svg>
              <span className="font-bold text-sm uppercase tracking-wider">Exclusive Parent Admission Session</span>
            </div>
          </div>

          {/* Pre-headline */}
          <p className="text-center text-amber-400 font-semibold text-sm uppercase tracking-widest mb-4">
            Before enrolling your teen in any financial program
          </p>

          {/* Main Headline */}
          <h2 className="font-serif text-5xl text-white mb-4 text-center leading-tight">
            What You'll Learn in the Parent Orientation
          </h2>
          
          {/* Subheadline */}
          <p className="text-center text-gray-300 text-lg mb-12 max-w-3xl mx-auto leading-relaxed">
            A private <strong className="text-amber-400">90-minute consultation</strong> designed for discerning parents who refuse to let their teen learn money the hard way
          </p>

          {/* Premium Floating Card */}
          <div className="relative">
            {/* Glow Effect */}
            <div className="absolute -inset-1 bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 rounded-2xl blur-lg opacity-30"></div>
            
            <Card className="relative p-12 bg-white border-4 border-amber-500 shadow-2xl rounded-2xl">
              
              {/* Numbered Questions */}
              <div className="space-y-10">
                
                {/* Question 1 */}
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-2xl shadow-lg ring-4 ring-amber-200">
                      1
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      "Why is my teen clueless about money — and is it too late?"
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-base">
                      → Discover the 3 critical gaps in school education that leave teenagers financially unprepared — and the exact window of time you have to fix it before adulthood.
                    </p>
                  </div>
                </div>

                {/* Question 2 */}
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-2xl shadow-lg ring-4 ring-amber-200">
                      2
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      "What financial skills actually matter for my teen's future?"
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-base">
                      → See the 5 foundational money skills that prevent costly mistakes in their 20s and 30s — skills schools will never teach but every teenager desperately needs.
                    </p>
                  </div>
                </div>

                {/* Question 3 */}
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-2xl shadow-lg ring-4 ring-amber-200">
                      3
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      "Is my teen too young to learn about investing and markets?"
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-base">
                      → Learn the proven age-appropriate framework that makes complex financial concepts simple, engaging, and perfectly suited for 13-17 year olds.
                    </p>
                  </div>
                </div>

                {/* Question 4 */}
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-2xl shadow-lg ring-4 ring-amber-200">
                      4
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      "How does this program actually work, week by week?"
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-base">
                      → Get a complete walkthrough of the 10-week curriculum, teaching methods, live sessions, and exactly what your teen will learn each step of the way.
                    </p>
                  </div>
                </div>

                {/* Question 5 */}
                <div className="flex items-start gap-5">
                  <div className="flex-shrink-0">
                    <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 via-yellow-500 to-amber-600 flex items-center justify-center text-slate-900 font-bold text-2xl shadow-lg ring-4 ring-amber-200">
                      5
                    </div>
                  </div>
                  <div className="flex-1 pt-2">
                    <h3 className="text-xl font-bold text-gray-900 mb-3 leading-tight">
                      "Is this program really the right fit for my child?"
                    </h3>
                    <p className="text-gray-700 leading-relaxed text-base">
                      → Clear, honest criteria to help you determine if this is the right timing and match for your teen. No pressure, just clarity and confidence.
                    </p>
                  </div>
                </div>

              </div>

              {/* Gold Divider */}
              <div className="my-10 relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t-2 border-amber-300"></div>
                </div>
                <div className="relative flex justify-center">
                  <span className="bg-white px-4 text-amber-600">
                    <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  </span>
                </div>
              </div>

              {/* Premium CTA Section */}
              <div className="text-center">
                <h4 className="text-2xl font-bold text-gray-900 mb-3">
                  Don't let your teen figure out money the expensive way
                </h4>
                <p className="text-gray-600 text-base mb-8 max-w-2xl mx-auto">
                  This 90-minute orientation could be the difference between financial confidence and costly mistakes. Reserve your seat in this exclusive session.
                </p>
                
                {/* Trust Signals */}
                <div className="flex items-center justify-center gap-5 text-sm text-gray-700 mb-8 flex-wrap">
                  <span className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200">
                    <Clock size={18} className="text-amber-600" />
                    <strong>90-Minute Session</strong>
                  </span>
                  <span className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200">
                    <Shield size={18} className="text-amber-600" />
                    <strong>Recording Included</strong>
                  </span>
                  <span className="flex items-center gap-2 bg-amber-50 px-4 py-2 rounded-full border border-amber-200">
                    <Users size={18} className="text-amber-600" />
                    <strong>Limited to 20 Students per Cohort</strong>
                  </span>
                </div>

                {/* Premium CTA Button */}
                <Button
                  onClick={handleRegisterClick}
                  className="w-full bg-gradient-to-r from-amber-500 via-yellow-500 to-amber-500 hover:from-amber-600 hover:via-yellow-600 hover:to-amber-600 text-slate-900 font-bold px-8 py-7 text-lg sm:text-xl transition-all shadow-xl hover:shadow-2xl hover:scale-105 duration-300 border-2 border-yellow-400 whitespace-normal min-h-[4rem]"
                >
                  Reserve Your Exclusive Seat – ₹{programData.orientationDetails.fee}
                </Button>
                
                <p className="text-xs text-gray-500 mt-6 uppercase tracking-wide">
                  Private Parent Orientation Required Before Program Enrollment
                </p>
              </div>

            </Card>
          </div>

        </div>
      </section>

      {/* Program Format */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-12 text-center">
            Program Format
          </h2>

          <div className="grid md:grid-cols-3 gap-6">
            <Card className="p-8 text-center bg-white border border-gray-200">
              <Clock className="mx-auto mb-4 text-blue-900" size={40} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {programData.programFormat.duration}
              </h3>
              <p className="text-gray-600">
                {programData.programFormat.sessions} Live Sessions
              </p>
            </Card>

            <Card className="p-8 text-center bg-white border border-gray-200">
              <Calendar className="mx-auto mb-4 text-blue-900" size={40} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {programData.programFormat.schedule}
              </h3>
              <p className="text-gray-600">
                {programData.programFormat.timing}
              </p>
            </Card>

            <Card className="p-8 text-center bg-white border border-gray-200">
              <Users className="mx-auto mb-4 text-blue-900" size={40} />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                Small Cohort
              </h3>
              <p className="text-gray-600">
                {programData.programFormat.capacity}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Comparison Section - With vs Without Financial Education */}
      <section className="py-16 px-6 bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="font-serif font-bold text-4xl md:text-5xl text-gray-900 mb-4 text-center">
            The Cost of Financial Ignorance
          </h2>
          <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
            Most teenagers enter adulthood without understanding how money works. Here's what typically happens: ⚠️
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Without Financial Education */}
            <Card className="p-8 bg-white border-l-8 border-red-500 shadow-xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-br from-red-500 to-red-600 rounded-full p-3 shadow-lg">
                  <X className="text-white" size={28} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900">
                  Without Financial Education ❌
                </h3>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <TrendingDown className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    First credit card in college → debt spiral by age 22
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingDown className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    No understanding of compound interest → missed investment years
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingDown className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Falls for "get rich quick" schemes and trading tips
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingDown className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    First salary → impulse spending, no savings plan
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingDown className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Age 30 → financially dependent, no emergency fund
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <TrendingDown className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Learns about investing after 10+ years of earning
                  </span>
                </li>
              </ul>
            </Card>

            {/* With Financial Education */}
            <Card className="p-8 bg-green-50 border-2 border-green-600">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-green-600 rounded-full p-2">
                  <CheckCircle className="text-white" size={24} />
                </div>
                <h3 className="text-2xl font-semibold text-gray-900">
                  With Financial Education
                </h3>
              </div>

              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Understands risk-reward before entering college
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Starts investing early → 10+ years of compound growth
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Can identify scams and unrealistic promises
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    First salary → automatic savings & disciplined allocation
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Age 30 → strong financial foundation, building wealth
                  </span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="text-blue-900 flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-700">
                    Makes informed decisions without relying on "tips"
                  </span>
                </li>
              </ul>
            </Card>
          </div>

          <div className="mt-10 text-center">
            <p className="text-xl text-gray-800 font-semibold mb-6">
              The difference? Learning financial thinking at the right age.
            </p>
            <Button
              onClick={handleRegisterClick}
              className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-6 text-lg transition-colors"
            >
              Secure Your Seat – Pay ₹{programData.orientationDetails.fee}
            </Button>
          </div>
        </div>
      </section>

      {/* Meet the Founder */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-12 text-center">
            Meet the Founder
          </h2>

          <div className="bg-white rounded-lg border border-gray-200 p-10">
            {/* Founder Portrait */}
            <div className="flex justify-center mb-8">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/2jlo7gj2_WhatsApp%20Image%202026-04-16%20at%207.03.10%20PM.jpeg"
                alt="Muskaan Dewan - Founder"
                className="w-64 h-auto max-h-80 rounded-lg object-contain border-2 border-gray-200"
              />
            </div>

            <h3 className="text-2xl font-semibold text-gray-900 mb-2 text-center">
              Muskaan Dewan
            </h3>
            <p className="text-lg text-gray-600 mb-6 text-center">
              Founder, Financial Markets Program
            </p>

            <div className="space-y-4 text-gray-700 leading-relaxed">
              <p>
                Muskaan holds a Bachelor's degree in Financial Markets and a Master's degree in Economics. 
                She has over two years of structured teaching experience, including mentoring students 
                one-on-one in financial literacy, market fundamentals, and disciplined decision-making.
              </p>

              <p>
                Previously associated with Brightchamps and financial institutions such as Motilal Oswal 
                and Stock Axis, she has worked closely with both students and parents to build structured 
                financial thinking at an early age.
              </p>

              <p>
                Her approach focuses on risk awareness, long-term reasoning, and institutional-style 
                clarity — not speculation or shortcuts. She believes financial thinking is a life skill, 
                not a shortcut to quick money.
              </p>

              {/* Founder's Why Story */}
              <div className="mt-6 p-6 bg-blue-50 border-l-4 border-blue-900 rounded-lg">
                <p className="text-gray-800 italic leading-relaxed">
                  "I started this program because I saw too many teenagers entering adulthood without basic 
                  financial understanding — not because schools failed them, but because financial education 
                  simply isn't part of the curriculum. After mentoring students one-on-one for over two years, 
                  I realized this gap affects every family. This program is what I wish existed when I was 15 
                  — a structured, institutional approach to understanding money, markets, and long-term thinking."
                </p>
                <p className="text-gray-700 font-medium mt-3">— Muskaan Dewan</p>
              </div>
            </div>

            {/* LinkedIn Profile Link */}
            <div className="mt-8 text-center">
              <a
                href="https://www.linkedin.com/in/muskaandewan/"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-blue-900 hover:text-blue-700 font-medium transition-colors"
              >
                <Linkedin size={20} />
                View LinkedIn Profile
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Inside Our Live Sessions Section - NEW */}
      <section className="py-16 px-6 bg-white">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-3 text-center">
            Inside Our Live Sessions
          </h2>
          <p className="text-center text-gray-600 mb-12 max-w-2xl mx-auto">
            Real curriculum snapshots from our interactive classes — see what your teen will actually learn
          </p>

          {/* Image Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Image 1 - What is a Sector */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/dmjfmbdv_Screenshot%20%284%29.png"
                alt="Market Sectors - Understanding different industries"
                className="w-full h-64 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Market Structure</h3>
                <p className="text-sm text-gray-600">Understanding sectors and industries</p>
              </div>
            </div>

            {/* Image 2 - Stop Loss */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/v5ldtenj_Screenshot%20%2855%29.png"
                alt="Stop-Loss concept - Risk management fundamentals"
                className="w-full h-64 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Risk Management</h3>
                <p className="text-sm text-gray-600">Stop-loss strategies & protection</p>
              </div>
            </div>

            {/* Image 3 - Cash Flow Statement */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/feaf5eif_Screenshot%20%2822%29.png"
                alt="Cash Flow Statement - Financial literacy fundamentals"
                className="w-full h-64 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Financial Statements</h3>
                <p className="text-sm text-gray-600">Understanding cash flow & accounting</p>
              </div>
            </div>

            {/* Image 4 - Advanced Technical Analysis */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/qyduemle_Screenshot%20%2825%29.png"
                alt="Advanced Technical Analysis - Markets, Trade, and Financial Dynamics"
                className="w-full h-64 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Technical Analysis</h3>
                <p className="text-sm text-gray-600">Chart reading & pattern recognition</p>
              </div>
            </div>

            {/* Image 5 - Flag Chart Pattern */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/c8cm24jj_Screenshot%20%2844%29.png"
                alt="Flag Chart Pattern - Real trading chart analysis"
                className="w-full h-64 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Real Chart Analysis</h3>
                <p className="text-sm text-gray-600">Identifying patterns in live markets</p>
              </div>
            </div>

            {/* Image 6 - Interactive Pattern Hunt */}
            <div className="bg-white rounded-lg border-2 border-gray-200 overflow-hidden hover:shadow-xl transition-shadow">
              <img
                src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/d6307gbq_Screenshot%20%2845%29.png"
                alt="Interactive Activity - Pattern Hunt exercise"
                className="w-full h-64 object-cover"
              />
              <div className="p-4">
                <h3 className="font-semibold text-gray-900 mb-1">Interactive Learning</h3>
                <p className="text-sm text-gray-600">Hands-on exercises & activities</p>
              </div>
            </div>
          </div>

          {/* Trust Builder Text */}
          <div className="mt-10 text-center">
            <p className="text-gray-700 text-lg mb-4">
              Every session combines <strong>theory, visuals, and hands-on practice</strong> — 
              designed to keep teenagers engaged and learning.
            </p>
            <p className="text-gray-600">
              No boring lectures. No overwhelming jargon. Just structured, interactive financial education.
            </p>
          </div>
        </div>
      </section>

      {/* Parent Testimonial - Restructured Layout with 2 Photos */}
      <section className="py-16 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-12 text-center">
            Parent Testimonial
          </h2>

          {/* Desktop: Photos LEFT, Testimonial RIGHT | Mobile: Photos TOP, Testimonial BELOW */}
          <div className="flex flex-col md:flex-row gap-8 items-center md:items-start">
            
            {/* Family Photos - FIRST (appears on top mobile, left desktop) */}
            <div className="w-full md:w-2/5 flex-shrink-0 space-y-6">
              {/* Photo 1: Family with son */}
              <div className="bg-white rounded-lg border-2 border-blue-200 p-4 shadow-md">
                <img
                  src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/cbzpdklp_WhatsApp%20Image%202026-02-23%20at%204.12.25%20PM.jpeg"
                  alt="Ajesh Kuniyil, Harsha Ajesh & Viaan - Family photo"
                  className="w-full h-auto object-cover rounded-lg"
                />
              </div>

              {/* Photo 2: Parents couple */}
              <div className="bg-white rounded-lg border-2 border-blue-200 p-4 shadow-md">
                <img
                  src="https://customer-assets.emergentagent.com/job_parent-orientation-1/artifacts/4dfaix6x_WhatsApp%20Image%202026-02-23%20at%204.13.06%20PM.jpeg"
                  alt="Ajesh Kuniyil & Harsha Ajesh - Parents"
                  className="w-full h-auto object-cover rounded-lg"
                />
              </div>
            </div>

            {/* Testimonial Card - SECOND (appears below mobile, right desktop) */}
            <div className="w-full md:w-3/5">
              <Card className="p-10 bg-blue-50 border-2 border-blue-200">
                
                {/* Real Family Badge */}
                <div className="flex items-center gap-2 mb-6 pb-4 border-b border-blue-200">
                  <CheckCircle size={20} className="text-green-600" />
                  <span className="text-sm font-semibold text-gray-700">Verified Student · Completed All 3 Levels · Dubai, UAE</span>
                </div>

                {/* Before/During/After Story */}
                <div className="mb-6">
                  <p className="text-lg text-gray-700 mb-4">
                    <span className="font-semibold text-gray-900">Before the program:</span> Viaan used to spend his 
                    pocket money impulsively on games and snacks without thinking twice.
                  </p>
                  <p className="text-lg text-gray-700 mb-4">
                    <span className="font-semibold text-gray-900">During the program:</span> By Week 3, he started 
                    asking us about our investment portfolio and why we chose certain mutual funds over others.
                  </p>
                  <p className="text-lg text-gray-700 mb-6">
                    <span className="font-semibold text-gray-900">After the program:</span> He now tracks his savings, 
                    understands compound interest, and even created a budget plan for his college fund.
                  </p>
                </div>

                {/* Key Results - NEW */}
                <div className="bg-white rounded-lg p-6 mb-6 border border-blue-200">
                  <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                    <TrendingUp size={20} className="text-green-600" />
                    Measurable Results in 10 Weeks:
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-2xl font-bold text-blue-900">100%</p>
                      <p className="text-sm text-gray-600">Session attendance</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-900">3 weeks</p>
                      <p className="text-sm text-gray-600">To show change</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-900">Age 14</p>
                      <p className="text-sm text-gray-600">Started learning</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-900">Lifetime</p>
                      <p className="text-sm text-gray-600">Skill gained</p>
                    </div>
                  </div>
                </div>

                {/* Main Quote */}
                <blockquote className="text-xl text-gray-800 leading-relaxed mb-6 italic border-l-4 border-blue-900 pl-6">
                  "At such a young age, Viaan developed a mature and disciplined approach towards money, investing, 
                  and long-term decision-making — far beyond what regular school education offers. He's now our little 
                  financial advisor at home. We genuinely wish we had learned this at his age."
                </blockquote>

                {/* What Made the Difference - NEW */}
                <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-lg p-5 mb-6 border border-amber-200">
                  <h4 className="font-semibold text-gray-900 mb-3 text-sm">What Made the Difference:</h4>
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700"><strong>Live interaction</strong> — Not recorded videos, real-time Q&A</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700"><strong>Small batch size</strong> — 20 students max, personal attention</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-gray-700"><strong>Structured curriculum</strong> — Week-by-week progression, not random topics</p>
                    </div>
                  </div>
                </div>

                {/* Attribution */}
                <p className="text-gray-700 font-medium">
                  — Ajesh Kuniyil & Harsha Ajesh, Parents of Viaan (Age 14), Dubai, UAE
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Who This Is For */}
      <section className="py-16 px-6 bg-blue-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-12 text-center">
            Who This Is For
          </h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start bg-white p-6 rounded-lg border border-gray-200">
              <BookOpen className="text-blue-900 mr-4 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-700">{programData.targetAudience[0]}</p>
            </div>
            <div className="flex items-start bg-white p-6 rounded-lg border border-gray-200">
              <BookOpen className="text-blue-900 mr-4 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-700">{programData.targetAudience[1]}</p>
            </div>
            <div className="flex items-start bg-white p-6 rounded-lg border border-gray-200">
              <BookOpen className="text-blue-900 mr-4 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-700">{programData.targetAudience[2]}</p>
            </div>
            <div className="flex items-start bg-white p-6 rounded-lg border border-gray-200">
              <BookOpen className="text-blue-900 mr-4 flex-shrink-0 mt-1" size={24} />
              <p className="text-gray-700">{programData.targetAudience[3]}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Parent Orientation CTA Section */}
      <section className="py-20 px-6 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-serif text-4xl md:text-5xl mb-6">
            Parent Orientation Session
          </h2>

          <p className="text-xl text-blue-100 mb-10 max-w-2xl mx-auto">
            Attend a live orientation session to understand the program structure, 
            curriculum, and enrollment process. Only 20 students accepted per cohort — enrollment opens immediately after orientation on a first-come-first-served basis.
          </p>

          <Card className="bg-white text-gray-900 p-8 mb-8 max-w-2xl mx-auto">
            {/* Scarcity Indicator - 20 Students Only */}
            <div className="bg-orange-50 border-l-4 border-orange-500 rounded-lg p-4 mb-6">
              <p className="text-orange-900 font-semibold text-center">
                Only 20 Students Per Cohort — First-Come-First-Served After Orientation
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-6 text-left mb-6">
              <div>
                <p className="text-sm text-gray-600 mb-1">Date</p>
                <p className="font-semibold">{programData.orientationDetails.date}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Time</p>
                <p className="font-semibold">{programData.orientationDetails.time}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Duration</p>
                <p className="font-semibold">{programData.orientationDetails.duration}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600 mb-1">Format</p>
                <p className="font-semibold">{programData.orientationDetails.format}</p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-700 leading-relaxed">
                {programData.orientationDetails.recordingNote}
              </p>
            </div>

            {/* Value Stack */}
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-6">
              <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Award size={20} className="text-green-600" />
                What's Included in Your ₹299 Orientation:
              </h4>
              <ul className="space-y-3 text-sm text-gray-700">
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>90-minute live interactive session with Q&A</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Session recording shared within 24 hours (lifetime access)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Downloadable 10-week program curriculum PDF</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Live financial literacy baseline assessment activity</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Priority enrollment access (orientation attendees enroll first)</span>
                </li>
                <li className="flex items-start gap-2">
                  <CheckCircle size={16} className="text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Direct interaction with the founder, Muskaan Dewan</span>
                </li>
              </ul>
            </div>

            {/* Value-Based Guarantee */}
            <div className="bg-blue-900 text-white rounded-lg p-6 mb-6">
              <div className="flex items-start gap-3">
                <Shield size={24} className="flex-shrink-0 mt-1" />
                <div>
                  <h4 className="font-semibold mb-2">Our Transparency Promise</h4>
                  <p className="text-sm text-blue-100 leading-relaxed">
                    You'll leave the orientation with complete clarity on the full 10-week curriculum, 
                    teaching methodology, session structure, and learning outcomes. Even if you choose not to enroll, 
                    you'll gain valuable insights into how financial education works. Zero pressure, 
                    complete transparency.
                  </p>
                </div>
              </div>
            </div>

            <div className="border-t pt-6">
              <div className="flex justify-between items-center mb-4">
                <span className="text-lg text-gray-700">Registration Fee</span>
                <span className="text-3xl font-bold text-gray-900">
                  ₹{programData.orientationDetails.fee}
                </span>
              </div>
              <p className="text-sm text-gray-600 mb-6">
                Limited to {programData.orientationDetails.seatsAvailable} seats
              </p>
            </div>
          </Card>

          <Button
            onClick={handleRegisterClick}
            className="bg-amber-600 hover:bg-amber-700 text-white px-12 py-7 text-xl transition-colors"
          >
            Register Now – ₹{programData.orientationDetails.fee}
          </Button>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif text-4xl text-gray-900 mb-4 text-center">
            Frequently Asked Questions
          </h2>
          <p className="text-center text-gray-600 mb-12">
            Everything parents ask before registering
          </p>

          <div className="space-y-6">
            {/* FAQ 1 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Is this stock trading or gambling? Will my child start trading?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    No. This is an <strong>educational program</strong>, not a trading course. We teach financial 
                    literacy, market structure, risk management, and long-term thinking — not day trading, stock tips, 
                    or shortcuts. Think of it as learning economics and decision-making, not betting on stocks.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 2 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    My child has no finance background. Will they understand?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Absolutely. The program is designed for <strong>complete beginners</strong>. We start from 
                    fundamentals — what money is, how interest works, what investing means. No prior knowledge required. 
                    If your child is curious and willing to learn, they'll do great.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 3 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    What if we can't attend the orientation live?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    No problem. The session recording will be shared with you within 24 hours, and you'll have 
                    <strong> lifetime access</strong>. You'll also get the curriculum PDF. However, attending live 
                    lets you ask questions directly to the founder.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 4 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    How is this different from free YouTube videos on finance?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    YouTube gives random, disconnected clips. This program is a <strong>structured 10-week 
                    curriculum</strong> with live mentorship, accountability, peer learning, and assessment. 
                    It's like the difference between watching cooking videos vs attending a culinary course.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 5 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    What happens after the orientation?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    After the orientation, you'll know the exact curriculum, teaching style, and program structure. 
                    If you decide to enroll your child, enrollment opens immediately on a 
                    <strong> first-come-first-served basis</strong> — we only accept 20 students per cohort 
                    to ensure quality mentorship. Spots fill quickly.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 6 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Is the ₹299 orientation fee refundable?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    The ₹299 is non-refundable, but you get <strong>full value</strong>: 90-minute live session, 
                    lifetime recording access, curriculum PDF, financial literacy assessment, and direct interaction 
                    with the founder. You'll walk away with clarity and insights even if you don't enroll in the program.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 7 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    Will my child get a certificate?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Yes. Students who complete the 10-week program receive a <strong>certificate of completion</strong>. 
                    However, the real value isn't the certificate — it's the knowledge, discipline, and long-term 
                    thinking skills your child will develop.
                  </p>
                </div>
              </div>
            </Card>

            {/* FAQ 8 */}
            <Card className="p-6 bg-white border border-gray-200">
              <div className="flex items-start gap-3">
                <HelpCircle className="text-blue-900 flex-shrink-0 mt-1" size={24} />
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2">
                    My child is very busy with school. Can they manage this?
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    Sessions are only <strong>1 hour, twice a week (Saturdays & Sundays)</strong> at 3:00 PM IST. 
                    It's designed to fit into a teenager's schedule without overwhelming them. Many students balance 
                    this program alongside school, sports, and hobbies.
                  </p>
                </div>
              </div>
            </Card>
          </div>

          {/* CTA after FAQ */}
          <div className="mt-12 text-center">
            <p className="text-lg text-gray-700 mb-6">
              Still have questions? Attend the orientation and get all your answers directly from the founder.
            </p>
            <Button
              onClick={handleRegisterClick}
              className="bg-blue-900 hover:bg-blue-800 text-white px-10 py-6 text-lg transition-colors"
            >
              Secure Your Seat – Pay ₹{programData.orientationDetails.fee}
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h3 className="font-serif text-2xl mb-4">
              Advanced Financial Markets Program
            </h3>
            <p className="text-gray-400">
              Building the next generation of financially literate leaders
            </p>
          </div>

          <div className="border-t border-gray-800 pt-8 text-center">
            <p className="text-sm text-gray-400 mb-4">
              <strong>Disclaimer:</strong> Educational program only. Not investment advice.
            </p>
            <p className="text-xs text-gray-500 mb-6 max-w-3xl mx-auto leading-relaxed">
              This program is for educational purposes only and does not constitute investment advice, stock recommendations, portfolio management, or financial advisory services.
            </p>
            <p className="text-sm text-gray-500">
              © {new Date().getFullYear()} Advanced Financial Markets Program. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
